import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, OnDestroy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AssetVersionDto } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilKeyChanged, filter, map, of, Subject, switchMap } from 'rxjs';
import { browserRenderableExtensions, GALLERY_DOWNLOAD_SIZE, galleryDownloadSizePreferenceList } from '../../../consts';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { calcOriginalUrl } from '../../../lib/calc-original-url';
import { calcResizedUrl } from '../../../lib/calc-resized-url';
import { getAllowedDownloadSizes } from '../../../lib/convert-gallery-download-size';
import { estimateResizedAssetSize } from '../../../lib/estimate-resized-asset-size';
import { PICFLOW_CONFIG } from '../../../picflow-config';
import { AssetService } from '../../../services/asset.service';
import { OnGetData } from '../../../services/dialog.service';
import { DownloadAssetService } from '../../../services/download-asset.service';
import { GalleryService } from '../../../services/gallery.service';
import { IosAppCommunicationService } from '../../../services/ios-app-communication.service';
import { StorageService } from '../../../services/storage.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { defaultExtensionDataMap } from '../../components/non-preview-file/non-preview-data';
import { AssetVersionDimensionsPipe } from '../../pipes/asset-version-dimensions.pipe';

@Component({
  selector: 'app-ios-download-dialog',
  imports: [
    ButtonDirective,
    FormsModule,
    CapitalizePipe,
    DataUnitsBigIntPipe,
    AssetVersionDimensionsPipe,
    SvgComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ios-download-dialog.component.html',
  styleUrl: './ios-download-dialog.component.scss',
})
export class IosDownloadDialogComponent implements OnGetData, OnDestroy {
  dialogName = 'ios-download';
  private assetService = inject(AssetService);
  private galleryService = inject(GalleryService);
  private storage = inject(StorageService);
  private iosAppCommunicationService = inject(IosAppCommunicationService);
  private downloadAssetService = inject(DownloadAssetService);
  private config = inject(PICFLOW_CONFIG);
  dialogOptions(): DialogOptions {
    return {};
  }

  close: () => void;
  private assetId$ = new Subject<string>();
  private galleryId$ = new Subject<string>();
  private versionId$ = new Subject<string | undefined>();

  readonly asset = toSignal(
    combineLatest([this.assetId$, this.galleryId$]).pipe(
      switchMap(([assetId, galleryId]) => this.assetService.getAsset(assetId, galleryId, { allowCache: true })),
      switchMap(asset => this.assetService.withUpdates([asset])),
      map(assets => assets[0])
    )
  );
  private readonly versions = toSignal(
    combineLatest([this.assetId$, this.galleryId$, this.versionId$]).pipe(
      switchMap(([assetId, galleryId, versionId]) => {
        if (!versionId) return of([] as AssetVersionDto[]);
        return this.assetService.getAssetVersions(assetId, galleryId);
      })
    )
  );
  private readonly versionId = toSignal(this.versionId$);

  private readonly gallery = toSignal(
    this.galleryId$.pipe(switchMap(galleryId => this.galleryService.getGallery(galleryId)))
  );
  private readonly galleryPermissions = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      distinctUntilKeyChanged('id'),
      switchMap(gallery => this.galleryService.getGalleryPermissions(gallery))
    )
  );

  readonly versionData = computed(() => {
    const asset = this.asset();
    const versions = this.versions();
    const versionId = this.versionId();
    if (!versionId) return asset?.version_data;
    return versions.find(v => v.id === versionId);
  });

  private readonly downloadMediaSize = computed(() => this.gallery()?.download_media_size);

  readonly allowedSizes = computed(() => {
    const download_media_size = this.downloadMediaSize();
    const permissions = this.galleryPermissions();
    const asset = this.asset();
    const version = this.versionData();
    if (!download_media_size || !permissions || !asset || !version) return;
    const resp = getAllowedDownloadSizes(download_media_size, permissions, asset, version);
    return [...resp.sizes, ...(resp.allowOriginalDownload ? [{ name: 'original', size: 0 }] : [])];
  });

  readonly currentSize = linkedSignal(() => {
    const allowedSizes = this.allowedSizes();
    if (!allowedSizes) return;
    const storageSize = this.storage.get<string>('iosDownloadDialogLastSize');
    return [...(storageSize ? [storageSize] : []), ...galleryDownloadSizePreferenceList].find(name =>
      allowedSizes.find(s => s.name === name)
    );
  });

  readonly mode = computed(() => {
    const versionData = this.versionData();
    const currentSize = this.currentSize();
    if (!versionData || !currentSize) return;

    if (currentSize !== 'original') return 'longPress';
    const extension = versionData.asset_name.split('.').pop()?.toLowerCase();
    const renderable = browserRenderableExtensions.includes(extension);
    return renderable ? 'longPress' : 'download';
  });

  private readonly imageUrl = computed(() => {
    const mode = this.mode();
    const currentSize = this.currentSize();
    const asset = this.asset();
    const version = this.versionData();
    if (mode !== 'longPress') return;
    if (currentSize === 'original') {
      return calcOriginalUrl(this.config.assetsEndpoint, asset, version);
    }
    return calcResizedUrl(
      this.config.assetsEndpoint,
      asset,
      version,
      `l${GALLERY_DOWNLOAD_SIZE[currentSize]}mi`,
      asset.watermark_enabled
    );
  });

  readonly urlExtension = computed(() => {
    const url = this.imageUrl();
    const version = this.versionData();
    if (!version) return;

    if (url) return url.split('.').pop().toUpperCase();
    return version.asset_name.split('.').pop()?.toUpperCase();
  });

  readonly downloadSize = computed(() => {
    const currentSize = this.currentSize();
    const version = this.versionData();
    if (!currentSize || !version) return;
    if (currentSize === 'original') return BigInt(version.size);
    return estimateResizedAssetSize(version, GALLERY_DOWNLOAD_SIZE[currentSize]);
  });

  readonly imageUrlWithDownload = computed(() => {
    const version = this.versionData();
    const url = this.imageUrl();
    if (!version || !url) return;

    const urlExtension = url.split('.').pop();
    const name = version.asset_name;
    const nameParts = name.split('.');
    nameParts.pop();
    const nameWithUrlExtension = `${nameParts.join('.')}.${urlExtension}`;
    return `${url}?download=${encodeURIComponent(nameWithUrlExtension)}`;
  });

  readonly noPreviewIcon = computed(() => {
    const asset = this.asset();
    if (!asset) return;
    return defaultExtensionDataMap.find(d => d.type === asset.type)?.iconUrl;
  });

  saveSize(size: string) {
    this.storage.set('iosDownloadDialogLastSize', size);
  }

  onGetData(data: { assetId: string; galleryId: string; versionId: string }, close: () => void) {
    this.close = close;
    this.assetId$.next(data.assetId);
    this.galleryId$.next(data.galleryId);
    this.versionId$.next(data.versionId);
    this.iosAppCommunicationService.setLinkPreview(true);
    this.downloadAssetService.reportSingleAssetDownload(data.assetId, data.galleryId, data.versionId);
  }

  ngOnDestroy(): void {
    this.iosAppCommunicationService.setLinkPreview(false);
  }
}
